import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { translations } from '../i18n/translations';

const LanguageContext = createContext(null);
const LANGUAGE_KEY = 'niva_language';

function loadLanguage() {
  try {
    const stored = localStorage.getItem(LANGUAGE_KEY);
    return stored === 'ar' ? 'ar' : 'en';
  } catch {
    return 'en';
  }
}

function buildT(language) {
  return (key, params) => {
    let text = translations[language]?.[key] ?? translations.en[key] ?? key;
    if (params && typeof text === 'string') {
      Object.entries(params).forEach(([paramKey, value]) => {
        text = text.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(value));
      });
    }
    return text;
  };
}

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(loadLanguage);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const t = useCallback((key, params) => buildT(language)(key, params), [language]);

  const saveLanguage = useCallback((lang) => {
    const next = lang === 'ar' ? 'ar' : 'en';
    setLanguage(next);
    try {
      localStorage.setItem(LANGUAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  const value = useMemo(
    () => ({
      language,
      isRtl: language === 'ar',
      t,
      saveLanguage,
    }),
    [language, t, saveLanguage]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

/** Override UI language for a subtree without changing document dir/lang. */
export function LanguageOverrideProvider({ language = 'en', children }) {
  const parent = useContext(LanguageContext);
  const t = useCallback((key, params) => buildT(language)(key, params), [language]);
  const value = useMemo(
    () => ({
      language,
      isRtl: language === 'ar',
      t,
      saveLanguage: parent?.saveLanguage || (() => {}),
    }),
    [language, t, parent?.saveLanguage]
  );
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
